const CORS_PROXIES = [
  (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
  (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
];

// Minimal URL keyword exclusion: only clear-cut non-photo assets
const URL_EXCLUDE = /logo|icon|favicon|sprite|tracking|pixel|beacon|blank|noimage|no-image|dummy|sample|ogp-default|telno|tel|phone|number|call|contact|mail|fax|access|map|recruit|banner|title|heading|catch|text|word|illust|btn/i;

// Excluded file extensions (for subImages)
const EXT_EXCLUDE = /\.(gif|svg|ico|woff2?)(\?|$)/i;

// Minimal exclusion for heroImage candidate
const HERO_EXT_EXCLUDE = /\.(gif|svg|ico)(\?|$)/i;
const HERO_URL_EXCLUDE = /favicon|logo|icon|telno|tel-|-tel|phone|title|heading/i;

function isHeroExcluded(src) {
  if (src.startsWith('data:') || src.startsWith('blob:')) return true;
  if (HERO_EXT_EXCLUDE.test(src)) return true;
  if (HERO_URL_EXCLUDE.test(src)) return true;
  return false;
}

function isImgExcluded(img) {
  const src =
    img.getAttribute('src') ||
    img.getAttribute('data-src') ||
    img.getAttribute('data-lazy-src') ||
    img.getAttribute('data-original');
  if (!src) return true;
  if (src.startsWith('data:') || src.startsWith('blob:')) return true;
  if (EXT_EXCLUDE.test(src)) return true;
  if (URL_EXCLUDE.test(src)) return true;

  // Skip only when BOTH width and height are explicitly set and either is < 200
  const w = parseInt(img.getAttribute('width'), 10);
  const h = parseInt(img.getAttribute('height'), 10);
  if (!isNaN(w) && !isNaN(h) && (w < 200 || h < 200)) return true;

  return false;
}

function resolveUrl(src, base) {
  try {
    return new URL(src, base).href;
  } catch {
    return null;
  }
}

async function fetchHtml(url) {
  for (const proxy of CORS_PROXIES) {
    try {
      const res = await fetch(proxy(url), { signal: AbortSignal.timeout(8000) });
      if (res.ok) return res.text();
    } catch {
      // try next proxy
    }
  }
  return null;
}

export async function scrapeImages(url) {
  const html = await fetchHtml(url);
  if (!html) return { heroImage: null, images: [] };

  const doc = new DOMParser().parseFromString(html, 'text/html');

  const base = doc.createElement('base');
  base.href = url;
  doc.head.insertBefore(base, doc.head.firstChild);

  // og:image/twitter:image → heroImage unconditionally (site owner's intent)
  let heroImage = null;
  const ogRaw =
    doc.querySelector('meta[property="og:image"]')?.content ||
    doc.querySelector('meta[name="twitter:image"]')?.content;
  if (ogRaw) {
    const resolved = resolveUrl(ogRaw, url);
    if (resolved) heroImage = resolved;
  }

  // If no og:image: 1) first jpg/jpeg/webp, 2) fallback to first png
  if (!heroImage) {
    const allImgs = Array.from(doc.querySelectorAll('img'));
    const getSrc = (img) =>
      img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('data-lazy-src') ||
      img.getAttribute('data-original');

    for (const img of allImgs) {
      const src = getSrc(img);
      if (!src || isHeroExcluded(src)) continue;
      if (!/\.(jpg|jpeg|webp)(\?|$)/i.test(src)) continue;
      const resolved = resolveUrl(src, url);
      if (resolved) { heroImage = resolved; break; }
    }

    if (!heroImage) {
      for (const img of allImgs) {
        const src = getSrc(img);
        if (!src || isHeroExcluded(src)) continue;
        if (!/\.png(\?|$)/i.test(src)) continue;
        const resolved = resolveUrl(src, url);
        if (resolved) { heroImage = resolved; break; }
      }
    }
  }

  // img tags → subImages pool (full filter, skip heroImage)
  const subImages = [];
  const addSub = (src) => {
    const resolved = resolveUrl(src, url);
    if (resolved && resolved !== heroImage && !subImages.includes(resolved)) {
      subImages.push(resolved);
    }
  };

  for (const img of doc.querySelectorAll('img')) {
    if (isImgExcluded(img)) continue;
    const src =
      img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('data-lazy-src') ||
      img.getAttribute('data-original');
    if (src) addSub(src);
  }

  for (const source of doc.querySelectorAll('picture source[srcset]')) {
    const first = source.getAttribute('srcset').split(',')[0].trim().split(' ')[0];
    if (first) {
      const resolved = resolveUrl(first, url);
      if (resolved && !EXT_EXCLUDE.test(resolved) && !URL_EXCLUDE.test(resolved) && resolved !== heroImage && !subImages.includes(resolved)) {
        subImages.push(resolved);
      }
    }
  }

  // Exclude png from subImages when 2+ jpg/webp alternatives exist
  const jpgWebpCount = subImages.filter(src => /\.(jpg|jpeg|webp)(\?|$)/i.test(src)).length;
  const finalImages = jpgWebpCount >= 2
    ? subImages.filter(src => !/\.png(\?|$)/i.test(src))
    : subImages;

  return {
    heroImage,
    images: finalImages.slice(0, 5),
  };
}
